<template>
  <div v-if="visible" class="sidebar-nav-group">
    <button
      v-if="collapsible && !collapsed && title"
      type="button"
      class="group-header"
      :aria-expanded="isExpanded"
      @click="toggleExpanded"
    >
      <span class="group-title">{{ title }}</span>
      <i
        class="fas fa-chevron-down group-chevron"
        :class="{ 'group-chevron--collapsed': !isExpanded }"
        aria-hidden="true"
      />
    </button>
    <div v-else-if="title && !collapsed" class="group-header-static">
      <span class="group-title">{{ title }}</span>
    </div>
    <div
      v-if="collapsed && showDividerWhenCollapsed"
      class="group-divider"
      aria-hidden="true"
    />

    <div
      class="group-items-wrapper"
      :class="{ 'group-items-wrapper--collapsed': !showItems }"
    >
      <div class="group-items-inner">
        <div class="group-items">
          <slot />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
const STORAGE_KEY = 'qrsconfig@sidebar-groups';

function readGroupStates() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function writeGroupState(groupId, expanded) {
  const states = readGroupStates();
  states[groupId] = expanded;
  localStorage.setItem(STORAGE_KEY, JSON.stringify(states));
}

export default {
  name: 'sidebar-nav-group',
  props: {
    title: {
      type: String,
      default: '',
    },
    groupId: {
      type: String,
      required: true,
    },
    collapsed: {
      type: Boolean,
      default: false,
    },
    visible: {
      type: Boolean,
      default: true,
    },
    showDividerWhenCollapsed: {
      type: Boolean,
      default: false,
    },
    routeNames: {
      type: Array,
      default: () => [],
    },
    defaultExpanded: {
      type: Boolean,
      default: true,
    },
    collapsible: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    const saved = readGroupStates();
    const hasSaved = Object.prototype.hasOwnProperty.call(saved, this.groupId);
    return {
      isExpanded: hasSaved ? saved[this.groupId] : this.defaultExpanded,
    };
  },
  computed: {
    showItems() {
      if (!this.collapsible) {
        return true;
      }
      return this.collapsed || this.isExpanded;
    },
  },
  watch: {
    '$route.name': {
      immediate: true,
      handler(routeName) {
        if (routeName && this.routeNames.includes(routeName)) {
          this.setExpanded(true);
        }
      },
    },
  },
  methods: {
    toggleExpanded() {
      this.setExpanded(!this.isExpanded);
    },
    setExpanded(value) {
      if (this.isExpanded === value) {
        return;
      }
      this.isExpanded = value;
      writeGroupState(this.groupId, value);
    },
  },
};
</script>

<style scoped>
.group-header,
.group-header-static {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  min-height: 22px;
  margin-bottom: 4px;
  padding: 0;
  border: none;
  border-radius: var(--border-radius-sm);
  background: transparent;
  cursor: pointer;
  transition: background 0.15s ease;
}

.group-header:hover,
.group-header:focus-visible {
  background: var(--state-hover);
  outline: none;
}

.group-header-static {
  cursor: default;
  pointer-events: none;
}

.group-title {
  color: var(--text-muted);
  font-size: 0.62rem;
  font-weight: var(--font-weight-semibold);
  text-transform: uppercase;
  letter-spacing: 0.4px;
  opacity: 0.75;
}

.group-chevron {
  font-size: 0.65rem;
  color: var(--text-muted);
  transition: transform 0.2s ease;
  flex-shrink: 0;
}

.group-chevron--collapsed {
  transform: rotate(-90deg);
}

.group-items-wrapper {
  display: grid;
  grid-template-rows: 1fr;
  transition: grid-template-rows 0.2s ease;
}

.group-items-wrapper--collapsed {
  grid-template-rows: 0fr;
}

.group-items-inner {
  overflow: hidden;
}

.group-items {
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 1px;
}

.group-divider {
  width: 60%;
  height: 1px;
  margin: var(--spacing-xs) auto;
  background: var(--border-sidebar);
  opacity: 0.6;
}
</style>
